import React, { useEffect, useState } from "react";
import { Anchor } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Circular reference photo for a port/city — resolved lazily via the backend
 * (which itself queries Wikipedia + Wikimedia Commons and caches for 30 days).
 * Falls back to an anchor glyph inside a neutral disc if the resolver returns
 * nothing. Photo URLs are cached client-side in localStorage under the key
 * `port_thumb_v1:<lowercased-name>` so navigation between pages is instant.
 */
const CACHE_PREFIX = "port_thumb_v1:";

function readCache(name) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + name.toLowerCase());
    if (!raw) return null;
    const j = JSON.parse(raw);
    if (!j || !j.at) return null;
    // Client cache lives 30 days — matches the server TTL for hits.
    if (Date.now() - j.at > 30 * 86_400_000) return null;
    return j;
  } catch { return null; }
}
function writeCache(name, url, source) {
  try {
    localStorage.setItem(CACHE_PREFIX + name.toLowerCase(), JSON.stringify({ url, source, at: Date.now() }));
  } catch { /* quota — ignore */ }
}

export default function PortThumbnail({ name, size = 88, className = "", testId, imageOverride = null }) {
  const trimmed = (name || "").trim();
  // Treat empty / placeholder names ("—", "-", "?", etc) as "no port
  // configured yet" so we render the elegant calm-sea placeholder
  // instead of triggering a Wikipedia lookup on garbage — which used to
  // return random faces / flags for the "—" token.
  const isPlaceholder = !trimmed || /^[—\-–?…]+$/.test(trimmed);
  // Priority: manager-picked override wins over cached auto-fetch wins
  // over live fetch. A non-empty override short-circuits the network
  // hop entirely so the picker's choice takes effect instantly.
  const [url, setUrl] = useState(() => {
    if (imageOverride) return imageOverride;
    if (isPlaceholder) return null;
    return readCache(trimmed)?.url || null;
  });
  const [loading, setLoading] = useState(() => !imageOverride && !isPlaceholder && !readCache(trimmed));

  useEffect(() => {
    // Override wins — jump straight to it and skip the resolver.
    if (imageOverride) { setUrl(imageOverride); setLoading(false); return; }
    if (isPlaceholder) { setUrl(null); setLoading(false); return; }
    const cached = readCache(trimmed);
    if (cached && cached.url) {
      setUrl(cached.url);
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    api.get(`/public/port-thumbnail?name=${encodeURIComponent(trimmed)}`)
      .then(({ data }) => {
        if (!live) return;
        setUrl(data?.url || null);
        writeCache(trimmed, data?.url || null, data?.source || null);
      })
      .catch(() => { if (live) setUrl(null); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [trimmed, isPlaceholder, imageOverride]);

  const dim = { width: size, height: size };
  // Placeholder branch — an elegant inline SVG of a still horizon so
  // cabins whose embark/disembark port hasn't been set yet still get a
  // beautiful, on-brand tile instead of a random face pulled by a
  // Wikipedia fallback. Pure SVG means zero network dependency (safe
  // for offline / air-gapped yachts) and it scales pixel-perfectly at
  // every timeline density.
  if (isPlaceholder) {
    return (
      <div
        className={`relative rounded-full overflow-hidden border border-border/70 flex items-center justify-center shrink-0 ${className}`}
        style={dim}
        data-testid={testId || "port-thumb-calm-sea"}
        title="Location to be confirmed"
        aria-label="Location to be confirmed"
      >
        <svg viewBox="0 0 100 100" className="w-full h-full block" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <defs>
            {/* Sky-to-sea gradient — soft golden-hour warmth up top,
                deepens into a muted teal at the horizon so the disc
                reads as "calm ocean" the moment it lands. */}
            <linearGradient id="port-calm-sky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f4e6c8" />
              <stop offset="55%" stopColor="#d9c9a1" />
              <stop offset="60%" stopColor="#6a8a94" />
              <stop offset="100%" stopColor="#2e4a58" />
            </linearGradient>
            <linearGradient id="port-calm-sun-ray" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(244,230,200,0.7)" />
              <stop offset="100%" stopColor="rgba(244,230,200,0)" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width="100" height="100" fill="url(#port-calm-sky)" />
          {/* Setting sun kiss just above the horizon */}
          <circle cx="50" cy="53" r="7" fill="#f6e4b7" opacity="0.9" />
          <ellipse cx="50" cy="60" rx="22" ry="1.2" fill="url(#port-calm-sun-ray)" opacity="0.9" />
          {/* Three whisper-thin wave marks so the "sea" reads as water,
              not a solid block. Opacities descend with depth. */}
          <path d="M0 66 Q25 63 50 66 T100 66" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="0.7" />
          <path d="M0 74 Q25 71 50 74 T100 74" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.6" />
          <path d="M0 84 Q25 81 50 84 T100 84" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="0.5" />
        </svg>
        {/* Tiny anchor mark centered on the horizon so users immediately
            understand this is a stand-in for a port that hasn't been
            named yet — otherwise it just looks like decorative art. */}
        <Anchor
          className="absolute text-white/70"
          style={{ width: size * 0.22, height: size * 0.22 }}
          strokeWidth={1.6}
        />
      </div>
    );
  }

  return (
    <div
      className={`relative rounded-full overflow-hidden border border-border/70 bg-muted flex items-center justify-center shrink-0 ${className}`}
      style={dim}
      data-testid={testId || `port-thumb-${trimmed}`}
      title={trimmed}
    >
      {url ? (
        <img
          src={url}
          alt={trimmed}
          className="w-full h-full object-cover"
          onError={() => setUrl(null)}
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      ) : (
        <Anchor
          className={`text-primary/40 ${loading ? "animate-pulse" : ""}`}
          style={{ width: size * 0.35, height: size * 0.35 }}
          strokeWidth={1.4}
        />
      )}
    </div>
  );
}
